import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  Paper,
  CircularProgress,
  Popper,
} from '@mui/material';
import debounce from 'lodash.debounce';
import gsap from 'gsap';
import { getVehicleSearchString } from '../../api/apiServices';
import { formatSearchDescription } from '../../utils/carBrand';

const sizeOverrides = {
  'vehicle-return': '324px',
  'vehicle-return-full': '662px',
  'vehicle-full': '448px',
  'accounts-half': '416px',
  'accounts-full': '864px',
  full: '1000px',
  half: '500px',
  medium: '403px',
  small: '301px',
};

/** ---------- helpers (SearchDescription ONLY) ---------- */
const getDesc = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v.SearchDescription === 'string') return v.SearchDescription;
  if (typeof v.VehicleDescription === 'string') return v.VehicleDescription;
  return '';
};

const getRawDesc = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v.trimEnd();
  if (typeof v.SearchDescriptionRaw === 'string')
    return v.SearchDescriptionRaw.trimEnd();
  return getDesc(v).trimEnd();
};

const normalizeVehicle = (vehicle, desc, rawDesc) => {
  const description = desc ?? getDesc(vehicle);
  const rawDescription = rawDesc ?? getRawDesc(vehicle);
  if (typeof vehicle === 'string' || !vehicle) {
    return {
      SearchDescription: description,
      SearchDescriptionRaw: rawDescription,
    };
  }
  return {
    ...vehicle,
    SearchDescription: description,
    SearchDescriptionRaw: rawDescription,
  };
};

const VehicleSearch = ({
  name,
  question,
  value,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  isMobile,
  vehicleYear,
  onFocusChange,
  layoutVariant = 'default',
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState('');
  const [anchorWidth, setAnchorWidth] = useState(0);

  const fieldWrapRef = useRef(null); // anchor for Popper
  const mousedownRef = useRef(false);
  const dropdownRef = useRef(null); // for the gsap fade-in

  /** sync from external value — use it exactly as provided (no formatting on load) */
  useEffect(() => {
    const incoming = getDesc(value);
    if (incoming) {
      setInputValue(getRawDesc(value));
      setSelected(normalizeVehicle(value, incoming));
      setError('');
      setValidationState?.(name, true);
    } else {
      setInputValue('');
      setSelected(null);
      setValidationState?.(name, false);
    }
  }, [value, name, setValidationState]);

  /** validation registration */
  const validate = () => {
    const isValid = !!getDesc(selected);
    if (!isValid && question?.validation?.required) {
      setError(question.validation.message || 'Required');
    } else {
      setError('');
    }
    setValidationState?.(name, isValid);
    return isValid;
  };

  useEffect(() => {
    registerValidation?.(name, validate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /** a year is only usable once it is a committed 4-digit value */
  const hasYear = /^\d{4}$/.test(String(vehicleYear ?? '').trim());

  /** fetch */
  const fetchOptions = async (input) => {
    if (input.length < 3) {
      setOptions([]);
      return;
    }
    if (!hasYear) {
      setOptions([]);
      setError('Please select a year of manufacture first');
      return;
    }
    setLoading(true);
    try {
      const results = await getVehicleSearchString(vehicleYear, input);
      setOptions(results || []);
    } catch (err) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce(fetchOptions, 300), [
    vehicleYear,
  ]);

  /** handlers */
  const handleClear = (e) => {
    e.preventDefault();
    setInputValue('');
    setSelected(null);
    setOptions([]);
    setError('');
    onChange?.(name, null);
    setValidationState?.(name, false);
  };

  const handleInput = (e) => {
    const input = e.target.value;
    setInputValue(input);
    setSelected(null);
    setError('');
    setValidationState?.(name, false);
    onChange?.(name, null);

    if (input.length >= 3) debouncedFetch(input);
    else {
      debouncedFetch.cancel?.();
      setOptions([]);
    }
  };

  const handleSelect = (vehicle) => {
    const raw = getDesc(vehicle).trimEnd();
    if (!raw) return;

    const formattedName = formatSearchDescription(raw);

    setInputValue(raw);
    setOptions([]);

    const updatedVehicle = normalizeVehicle(vehicle, formattedName, raw);
    setSelected(updatedVehicle);
    onChange?.(name, updatedVehicle);

    setValidationState?.(name, true);
    setFocused(false);
    onFocusChange?.(false);
    onNext?.();
  };

  const handleFocus = () => {
    setFocused(true);
    onFocusChange?.(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!mousedownRef.current) {
        setFocused(false);
        onFocusChange?.(false);
      }
    }, 0);
  };

  /** global mousedown guard to prevent blur-before-click */
  useEffect(() => {
    const handleMouseDown = () => (mousedownRef.current = true);
    const handleMouseUp = () => (mousedownRef.current = false);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  /** animate dropdown in */
  useEffect(() => {
    if (focused && options.length > 0 && dropdownRef.current) {
      gsap.fromTo(
        dropdownRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out' }
      );
      // keep width in sync
      const w = fieldWrapRef.current?.getBoundingClientRect()?.width ?? 0;
      setAnchorWidth(w);
    }
  }, [focused, options]);

  /** keep Popper width synced on resize */
  useLayoutEffect(() => {
    const onResize = () => {
      const w = fieldWrapRef.current?.getBoundingClientRect()?.width ?? 0;
      setAnchorWidth(w);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** sizing */
  const fieldWidth = question?.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;

  const fieldHeight = isMobile ? '51px' : '53px';
  const fontSize = '1rem';
  const dropdownActive = focused && options.length > 0;

  const placeholderText = inputValue
    ? ''
    : question?.placeholder || question?.label || '';

  return (
    <Box
      ref={fieldWrapRef}
      sx={{
        width: responsiveWidth,
        mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        position: 'relative',
        maxWidth: '100%',
        zIndex: 1,
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        label={inputValue ? question?.label : undefined}
        placeholder={placeholderText}
        value={inputValue}
        onChange={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: loading ? (
            <CircularProgress size={20} />
          ) : inputValue ? (
            <span
              className="material-symbols-rounded"
              onMouseDown={handleClear}
              style={{
                fontWeight: 400,
                fontSize: 20,
                color: '#738289',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              close
            </span>
          ) : null,
        }}
        InputLabelProps={{
          shrink: Boolean(inputValue),
          sx: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            letterSpacing: '-0.02em',
            color: '#738289',
          },
        }}
        sx={{
          height: fieldHeight,
          '& .MuiOutlinedInput-root': {
            height: fieldHeight,
            minHeight: fieldHeight,
            borderRadius: dropdownActive ? '12px 12px 0 0' : '12px',
            padding: '16px 12px',
            fontFamily: 'Inter, sans-serif',
            fontSize,
            letterSpacing: '-0.02em',
            color: inputValue ? '#162F3B' : '#738289',
            backgroundColor: '#fff',
            '& fieldset': {
              borderRadius: dropdownActive ? '12px 12px 0 0' : '12px',
              borderColor: '#A2ACB1',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize,
              lineHeight: '1.2rem',
              letterSpacing: '-0.02em',
              color: inputValue ? '#162F3B' : '#738289',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&::placeholder': { color: '#738289', opacity: 1 },
            },
          },
          '& .MuiFormHelperText-root': {
            textAlign: 'right',
            fontSize: isMobile ? '0.6rem' : '0.75rem',
            color: '#D32F2F',
            fontFamily: 'Inter, sans-serif',
            mt: '0px',
            mx: 0,
            pr: '8px',
          },
        }}
      />

      {/* ⬇️ Render the menu in a Portal so nothing can overlay it */}
      <Popper
        open={dropdownActive}
        anchorEl={fieldWrapRef.current}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 0] } },
          { name: 'flip', enabled: false },
        ]}
        sx={{ zIndex: 5000 }}
      >
        <Paper
          ref={dropdownRef}
          elevation={3}
          sx={{
            width: anchorWidth || 'auto',
            maxWidth: '100%',
            backgroundColor: '#fff',
            borderRadius: '0 0 20px 20px',
            border: '1px solid #A2ACB1',
            borderTop: 'none',
            boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
            overflowY: 'auto',
            maxHeight: '250px',
            pointerEvents: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          <List disablePadding>
            {options.map((opt, index) => (
              <ListItem key={index} disableGutters>
                <Box
                  onMouseDown={() => (mousedownRef.current = true)}
                  onClick={() => handleSelect(opt)}
                  onMouseUp={() => (mousedownRef.current = false)}
                  sx={{
                    width: '100%',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.875rem',
                      color: '#162F3B',
                      whiteSpace: isMobile ? 'normal' : 'nowrap',
                      overflow: 'hidden',
                      textOverflow: isMobile ? 'clip' : 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: isMobile ? 2 : 1,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {getDesc(opt) || '[Invalid vehicle]'}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default VehicleSearch;
